import {
  Avatar,
  ButtonBase,
  Divider,
  Link,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { MouseEvent, useState } from 'react'
import { Trans } from 'react-i18next'
import { Button, IconButton, InformationModal, SkeletonText } from 'utoniq-ui'

import {
  ConsoleEventDetailCollectionBoxFragment,
  ItemType,
} from '@/clients/utoniq-core/schema'
import { CollectionStatusCell } from '@/components/organisms/console/ConsoleEventItem/CollectionStatusCell'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { Action, State } from '@/components/pages/ConsoleEventDetail/template'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'
import { DateTime } from '@/core/entities'

type CollectionEventCellProps = {
  eventId: string
  collections: State['collections']
  hasNextPage: boolean
  loading: boolean
  removingCollection: boolean
  onFetchMore: Action['onFetchMore']
  onRemoveCollectionFromEvent: (collectionId: string) => Promise<void>
}
export const CollectionEventCell: Cell<CollectionEventCellProps> = ({
  eventId,
  collections,
  hasNextPage,
  loading,
  removingCollection,
  onFetchMore,
  onRemoveCollectionFromEvent,
}) => {
  const { t } = useTranslation('consoleEventDetail')
  const { routerPages } = useRouter()

  return (
    <CellFrame sx={{ pt: 3, px: 5, pb: 5 }}>
      {loading && !collections ? (
        <SkeletonText variant="h5" sx={{ pb: 5 }} />
      ) : (
        <Stack spacing={2} pb={5}>
          <Typography variant="h5">{t('registrationCollection')}</Typography>
          <Typography variant="caption">
            <Trans
              i18nKey="registrationCollectionNote"
              t={t}
              components={{
                itemLinkTag: (
                  <Link variant="caption" href={routerPages.ConsoleItems} />
                ),
                collectionLinkTag: (
                  <Link
                    variant="caption"
                    href={routerPages.ConsoleCollections}
                  />
                ),
              }}
            />
          </Typography>
        </Stack>
      )}
      <Stack spacing={4}>
        <ScrollFetcher
          nodes={collections?.map((eventCollection, index) => (
            <Stack key={eventCollection.collectionId} spacing={4}>
              <CollectionItemCell
                eventCollection={eventCollection}
                eventId={eventId}
                removingCollection={removingCollection}
                onRemoveCollectionFromEvent={onRemoveCollectionFromEvent}
              />
              {collections?.length - 1 > index && <Divider />}
            </Stack>
          ))}
          emptyMessage={t('collectionItemEmptyMessage')}
          loading={loading}
          hasNextPage={hasNextPage}
          onFetchMore={onFetchMore}
          loadingComponent={
            <Stack sx={{ py: 4 }}>
              <CollectionItemSkeletonCell />
            </Stack>
          }
        />
      </Stack>
    </CellFrame>
  )
}

type CollectionItemCellProps = {
  eventCollection: ConsoleEventDetailCollectionBoxFragment
  eventId: string
  removingCollection: boolean
  onRemoveCollectionFromEvent: (collectionId: string) => Promise<void>
}
const CollectionItemCell: Cell<CollectionItemCellProps> = ({
  eventCollection,
  eventId,
  removingCollection,
  onRemoveCollectionFromEvent,
}) => {
  const { t } = useTranslation('consoleEventDetail')
  const { router, generatePath, routerPages } = useRouter()
  const { showMessage, showError } = useMessage()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [openDeleteCollectionModal, setOpenDeleteCollectionModal] =
    useState<boolean>(false)

  // チケットアイテムを含むかどうか。含む場合、受け取り管理ボタンを表示する
  const hasTicketItem = eventCollection.collection.collectionItems.edges.some(
    ({ node }) => node.item.type === ItemType.Ticket
  )

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleRemoveCollection = () => {
    onRemoveCollectionFromEvent(collection.id)
      .then(() => showMessage(t('removeCollectionSuccess'), 'success'))
      .finally(() => setOpenDeleteCollectionModal(false))
      .catch(showError)
  }

  if (!eventCollection) return null

  const { collection } = eventCollection
  // 受け取り可能時間
  const pickupTimeArr = (eventCollection.pickupTime ?? [])
    .map(({ options }) => DateTime.formatDate(options.time, 'HH:mm'))
    .sort()
  const pickupTimeRange =
    pickupTimeArr.length > 1
      ? `${pickupTimeArr[0]}...${pickupTimeArr[pickupTimeArr.length - 1]}`
      : pickupTimeArr[0]

  return (
    <CellFrame>
      <Stack direction="row" spacing={2} sx={{ pb: 1 }}>
        <ButtonBase
          sx={{ flex: 1, textAlign: 'left' }}
          href={generatePath(routerPages.ConsoleCollectionDetail, {
            collectionId: eventCollection.collectionId,
          })}
        >
          <Stack direction="row" flex={1} spacing={3}>
            <Avatar
              variant="square"
              src={collection.thumbnail.uri ?? ''}
              sx={{ width: 120, height: 90 }}
            />
            <Stack flex={1}>
              <Stack direction="row" spacing={2} alignItems="center">
                {!!collection.status && (
                  <CollectionStatusCell status={collection.status} />
                )}
              </Stack>
              <Typography variant="h6">{collection.title}</Typography>
              <Stack sx={{ pt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('createdAt', { date: collection.formattedCreatedAt })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('itemCount', { number: collection.itemCount })}
                </Typography>
                {!!pickupTimeArr.length && (
                  <Typography variant="caption" color="text.secondary">
                    {t('deliveryTime', { delivery: pickupTimeRange })}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>
        </ButtonBase>

        <IconButton.More
          size="xSmall"
          bgcolor="grey.100"
          color="grey.700"
          onClick={handleClick}
          sx={{
            position: 'relative',
            zIndex: 2,
            transform: 'rotate(90deg)',
          }}
        />
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          {hasTicketItem && (
            <MenuItem
              onClick={() =>
                router.push(
                  generatePath(
                    routerPages.ConsoleEventCollectionReceiveTimeEdit,
                    {
                      collectionId: collection.id,
                      eventId,
                    }
                  )
                )
              }
            >
              {t('editDeliveryTime', '受け渡し時間の編集')}
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              setOpenDeleteCollectionModal(true)
              setAnchorEl(null)
            }}
          >
            {t('removeCollection', 'このストアから削除')}
          </MenuItem>
        </Menu>
      </Stack>
      {hasTicketItem && (
        <Stack
          direction="row"
          spacing={2}
          sx={{ mt: 2, a: { fontSize: '13px' } }}
        >
          <Button
            fullWidth
            variant="outlined"
            href={generatePath(routerPages.ConsoleTicketReceives, {
              collectionId: collection.id,
              eventId,
            })}
          >
            {t('receiptManagement', '受け取り管理')}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            href={generatePath(routerPages.ConsoleQuantityManagements, {
              collectionId: collection.id,
              eventId,
            })}
          >
            {t('quantityManagement', '商品数量の確認')}
          </Button>
        </Stack>
      )}
      <InformationModal
        title={t('deleteCollectionModalTitle')}
        content={t('deleteCollectionModalDesc')}
        open={openDeleteCollectionModal}
        onClose={() => setOpenDeleteCollectionModal(false)}
        cancelText={t('close')}
        buttons={
          <Button
            color="primary"
            size="large"
            loading={removingCollection}
            disabled={removingCollection}
            onClick={handleRemoveCollection}
            sx={{ minWidth: 212 }}
          >
            {t('deleteCollectionBtn')}
          </Button>
        }
      />
    </CellFrame>
  )
}

const CollectionItemSkeletonCell = () => (
  <CellFrame>
    <Stack direction="row" spacing={2}>
      <Stack direction="row" flex={1} spacing={3}>
        <Skeleton
          variant="rectangular"
          width={120}
          height={90}
          sx={{ borderRadius: 2 }}
        />
        <Stack flex={1} spacing={1}>
          <Stack direction="row" spacing={1}>
            <Stack flex={1}>
              <SkeletonText variant="large" />
            </Stack>
            <Stack flex={5} justifyContent="center">
              <SkeletonText variant="small" />
            </Stack>
          </Stack>
          <SkeletonText variant="h3" />
          <Stack>
            <SkeletonText variant="small" numberOfLine={3} />
          </Stack>
        </Stack>
      </Stack>
      <Skeleton variant="circular" width={28} height={28} />
    </Stack>
  </CellFrame>
)
